import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock-upgrade';
import * as focusTrap from 'focus-trap';
import { gsap, Power2 } from 'gsap/all';
import InlineSearch from '@pratt/js/components/algolia/inline-search';
import NavSearchModal from '@pratt/js/components/nav-search-modal';
import Tabs from './tabs';

class Menu {
  constructor(classname) {
    this.navCheck = document.body.classList.contains('nav-v2');
    this.classname = classname;

    this.rootEl = document.querySelector(this.classname);
    this.menuTrigger = document.querySelector(`${this.classname}-trigger`);
    this.menuInner = document.querySelector('.menu-inner');
    if (this.navCheck) {
      this.menuSearchTrigger = document.querySelector(`${this.classname}-search`);
      this.menuCloseEls = document.querySelectorAll(`${this.classname}-close`);
      this.menuToggleAll = document.querySelector(`${this.classname}-toggle-all`);
      this.menuToggleSchool = document.querySelector(`${this.classname}-toggle-school`);
      this.menuContentAll = document.querySelector('.menu-content--all');
      this.menuContentSchool = document.querySelector('.menu-content--school');
      this.menuSearch = document.querySelector('.menu-search');
      this.tabButtons = document.querySelectorAll('.menu__toggle-option');
    } else {
      this.menuClose = document.querySelector(`${this.classname}-close`);
      this.lineEl = this.rootEl.querySelector('.js-menu-line');
      this.currentStateEl = '';
      this.currentTab = '';
      this.tl = gsap.timeline();
      this.tabButtons = document.querySelectorAll('.menu-tab');
      document.body.classList.add('menu-is-animating');

      const searchRootEl = document.querySelector('.js-nav-search');
      if (searchRootEl) {
        if (searchRootEl.getAttribute('aria-modal') === 'true') {
          this.navSearch = new NavSearchModal();
        } else {
          this.navSearch = new InlineSearch(
            '.js-nav-search',
            '.js-nav-search-input',
            '.js-nav-search-hits',
          );
        }
      }

      this.tabsWrapper = document.querySelector('.js-menu-tabs');
      this.tabs = new Tabs(this.tabsWrapper, 'horizontal');
    }

    this.isOpen = false;
    if (this.navCheck) {
      this.focusTrap = focusTrap.createFocusTrap(this.rootEl, {
        escapeDeactivates: false,
        initialFocus: this.menuCloseEls[0],
        setReturnFocus: this.menuToggle,
      });
    } else {
      this.focusTrap = focusTrap.createFocusTrap(this.rootEl, {
        escapeDeactivates: false,
        initialFocus: this.menuClose,
        setReturnFocus: this.menuToggle,
      });
    }

    this.menuTrigger.addEventListener('click', () => {
      this.isOpen = !this.isOpen;
      this.update('menu');
    });

    if (this.navCheck) {
      this.menuSearchTrigger.addEventListener('click', () => {
        this.isOpen = !this.isOpen;
        this.menuSearch.focus();
        try {
          this.update('search');
        } catch (e) {
          // if focusTrap fails
          setTimeout(() => {
            document.querySelector('.menu-search').focus();
          }, 100);
        }
      });
      this.menuCloseEls.forEach(el => {
        el.addEventListener('click', () => {
          this.isOpen = false;
          this.update('close-menu');
        });
      });
    } else {
      this.menuClose.addEventListener('click', () => {
        this.isOpen = false;
        this.update();
      });
    }

    if (this.menuToggleAll) {
      this.menuToggleAll.addEventListener('click', () => {
        this.menuContentAll.classList.add('menu-content--active');
        this.menuContentSchool.classList.remove('menu-content--active');
        this.menuToggleAll.classList.add('tab-active');
        this.menuToggleSchool.classList.remove('tab-active');
      });
    }

    if (this.menuToggleSchool) {
      this.menuToggleSchool.addEventListener('click', () => {
        this.menuContentAll.classList.remove('menu-content--active');
        this.menuContentSchool.classList.add('menu-content--active');
        this.menuToggleAll.classList.remove('tab-active');
        this.menuToggleSchool.classList.add('tab-active');
      });
    }

    document.addEventListener('keydown', event => {
      if (this.isOpen && event.key === 'Escape') {
        event.preventDefault();
        this.isOpen = false;
        this.update('close-menu');
      }
    });

    this.init();
  }

  init() {
    if (!this.navCheck) {
      this.tabsWrapper.addEventListener('tab-state', this.tabState.bind(this));
    }
  }

  tabState(e) {
    this.currentStateEl = e.detail;

    if (this.currentTab === '') {
      this.animateIn(e);
      return;
    }

    if (this.currentTab !== e.detail.target) {
      this.animateLine(e);
    } else {
      return;
    }
  }

  disableTabs() {
    this.tabButtons.forEach(tab => tab.setAttribute('disabled', true));
  }

  enableTabs() {
    this.tabButtons.forEach(tab => tab.removeAttribute('disabled'));
  }

  animateIn(e) {
    const hasReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const duration = hasReducedMotion ? 0 : 0.5;
    this.positionLine(e.detail);

    gsap.to('line', {
      duration: duration,
      strokeDashoffset: 0,
      ease: Power2.easeInOut,
      onComplete: () => {
        this.currentTab = e.detail.target;
        this.currentTab.classList.add('tab-active');
        document.body.classList.remove('menu-is-animating');
      },
    });
  }

  animateLine(e) {
    const hasReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const duration = hasReducedMotion ? 0 : 0.5;

    this.disableTabs();
    this.currentTab.classList.remove('tab-active');
    document.body.classList.add('menu-is-animating');

    this.tl.to('.menu-panels', {
      duration: 0,
      opacity: 0,
      ease: Power2.easeInOut,
    });

    this.tl.to(
      'line',
      {
        duration: duration,
        strokeDashoffset: 1500,
        ease: Power2.easeInOut,
        onComplete: () => {
          this.positionLine(e.detail);
        },
      },
      '-=0.3',
    );

    this.tl.to('line', {
      duration: duration,
      strokeDashoffset: 0,
      ease: Power2.easeInOut,
    });

    this.tl.to(
      '.menu-panels',
      {
        duration: duration,
        opacity: 1,
        ease: Power2.easeInOut,
        onComplete: () => {
          this.currentTab = e.detail.target;
          this.currentTab.classList.add('tab-active');
          document.body.classList.remove('menu-is-animating');
          this.enableTabs();
        },
      },
      '-=0.3',
    );
  }

  positionLine(el) {
    const adminOffset = document.querySelector('.admin-bar') ? 32 : 0;
    const elRect = el.target.getBoundingClientRect();
    const menuGroupEl = el.tabPanel.querySelector('.menu-groups');
    const menuItems = menuGroupEl.querySelectorAll('.menu-groups__item');
    const tabPanelRect = menuGroupEl.getBoundingClientRect();
    const lineElHeight = tabPanelRect.top - (elRect.top + elRect.height + 30);

    if (menuItems.length <= 1) {
      document.querySelector(`[data-line-index="1"]`).classList.add('menu-line-hide');
    } else {
      document.querySelector(`[data-line-index="1"]`).classList.remove('menu-line-hide');
    }

    menuItems.forEach((item, index) => {
      const line = document.querySelector(`[data-line-index="${index}"]`);
      const itemRect = item.querySelector('.menu-item__link').getBoundingClientRect();

      if (index < 2) {
        line.setAttribute('x1', elRect.left + elRect.width / 2);
        line.setAttribute('x2', itemRect.left + itemRect.width / 2);
      }
    });

    if (tabPanelRect.top <= elRect.top + elRect.height + 30) {
      this.lineEl.style.cssText = `
      display: none;
    `;
    } else {
      this.lineEl.style.cssText = `
      top: ${elRect.top + elRect.height - adminOffset}px;
      height: ${lineElHeight}px;
    `;
    }
  }

  /**
   * Returns the classname without the starting `.`
   *
   * @example
   * this.classname = '.js--menu';
   * // this.plainClassname === 'js--menu'
   */
  get plainClassname() {
    return this.classname.replace(/^\.*/g, '');
  }

  get activeClass() {
    return `${this.plainClassname}-active`;
  }

  update(trigger) {
    this.rootEl.setAttribute('aria-hidden', !this.isOpen);
    this.menuTrigger.setAttribute('aria-expanded', this.isOpen);

    if (this.isOpen) {
      if (!this.navCheck) {
        this.focusTrap.activate();
      }
      disableBodyScroll(this.menuInner);
      if (!this.navCheck) {
        this.positionObserver = new ResizeObserver(() => {
          this.positionLine(this.currentStateEl);
        });
        this.positionObserver.observe(this.rootEl);
        // Make sure the first tab is selected
        this.tabs.resetTabs();
      } else {
        if (trigger === 'search') {
          this.focusTrap.activate();
        }
      }
    } else {
      this.focusTrap.deactivate();
      enableBodyScroll(this.menuInner);
      if (!this.navCheck) {
        this.positionObserver.disconnect();
        this.positionObserver = null;
      }
    }
  }
}

export default Menu;
